/**
 * @file LanguageFlag.tsx
 * @brief Circular flag image for a language row (flagcdn.com).
 *
 * @details
 * flagcdn only serves fixed widths (w20/w40/w80/w160/…). Passing an
 * unsupported size (e.g. 112) 404s and shows a broken-image glyph — which
 * is what broke the boardroom language stack. (28-07-2026)
 *
 * @author DES
 * @date 19-06-2026
 */

import { useState } from "react";
import { Earth } from "lucide-react";
import { cn } from "@/shared/theme/cn";
import {
  languageFlagUrl,
  type LanguageFlagInput,
} from "@/shared/data/language-flags";

interface LanguageFlagProps {
  language: LanguageFlagInput;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const SIZE: Record<NonNullable<LanguageFlagProps["size"]>, string> = {
  sm: "h-5 w-5",
  md: "h-10 w-10",
  lg: "h-14 w-14",
};

/** flagcdn CDN widths only — never invent intermediate sizes. */
const CDN_WIDTH: Record<NonNullable<LanguageFlagProps["size"]>, number> = {
  sm: 40,
  md: 80,
  lg: 160,
};

export function LanguageFlag({
  language,
  size = "md",
  className,
}: LanguageFlagProps) {
  const [broken, setBroken] = useState(false);
  const src = languageFlagUrl(language, CDN_WIDTH[size]);

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-border/80 bg-muted/30",
        SIZE[size],
        className,
      )}
    >
      {broken ? (
        <Earth
          className="h-1/2 w-1/2 text-muted-foreground"
          aria-hidden
        />
      ) : (
        <img
          key={src}
          src={src}
          alt=""
          aria-hidden
          draggable={false}
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      )}
    </span>
  );
}
